import React, { useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import gql from 'graphql-tag';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useMutation } from '@apollo/react-hooks';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { AccountContext } from '../../../components/AccountContext';
import { defineMessages, useIntl } from 'react-intl';
import { notificationMessages } from '../../../i18n/common';

const messages = defineMessages({
  header: {
    id: 'account-hierarchy.confirm-delete-account-modal.header',
    defaultMessage: 'Warning',
  },
  firstParagraph: {
    id: 'account-hierarchy.confirm-account-in-hierarchy-delete-account-modal.first-paragraph',
    defaultMessage:
      'You have chosen to delete account {accountId} - {accountName} but in order to do this, the following needs to be taken into consideration first',
  },
});

const DELETE_ACCOUNT = gql`
  mutation($input: DeleteAccountInput!) {
    deleteAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const CHECK_ACCOUNT_TO_DELETE = gql`
  mutation($input: DeleteAccountInput!) {
    checkAccountHierarchyBeforeDeletion(input: $input) {
      id
      displayName
      type
      ownerFullName
      ownerEmail
    }
  }
`;

const AccountsInHierarchyDeleteDialog = ({
  accountToDelete,
  accountsInHierarchyToDelete,
  hide,
  setAccountToDelete,
  setAccountsInHierarchyToDelete,
  fetchData,
}) => {
  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const [checkAccountToDeleteMutation] = useMutation(CHECK_ACCOUNT_TO_DELETE);
  const [deleteAccountMutation, { loading }] = useMutation(DELETE_ACCOUNT);

  const handleClick = async (account) => {
    try {
      const result = await checkAccountToDeleteMutation({
        variables: {
          input: {
            sourceAccountId: currentAccount,
            targetAccountId: account.id,
          },
        },
      });

      const accountsInHierarchyToDelete = result.data.checkAccountHierarchyBeforeDeletion;

      if (accountsInHierarchyToDelete.length === 0) {
        deleteAccountMutation({
          variables: {
            input: {
              sourceAccountId: currentAccount,
              targetAccountId: account.id,
            },
          },
        }).then(() => {
          fetchData();
          hide();
          setAccountsInHierarchyToDelete([]);

          growl.show({ severity: 'success', detail: formatMessage(notificationMessages.success) });
        });
      } else {
        hide();
        setAccountToDelete({
          id: account.id,
          brandDisplayName: account.displayName,
        });
        setAccountsInHierarchyToDelete(result.data.checkAccountHierarchyBeforeDeletion);
      }
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
    }
  };

  const header = <div className="dashboard-panel-header">{formatMessage(messages.header)}</div>;

  const deleteTemplate = (data) => (
    <Button
      icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
      className="p-button-secondary"
      disabled={loading}
      onClick={() => handleClick(data)}
    />
  );

  return (
    <Dialog visible onHide={hide} header={header} modal>
      <p>
        {formatMessage(messages.firstParagraph, {
          accountId: accountToDelete.id,
          accountName: accountToDelete.brandDisplayName,
        })}
      </p>
      <DataTable value={accountsInHierarchyToDelete} style={{ textAlign: 'center' }} paginator rows={5} responsive>
        <Column field="id" style={{ width: '12em' }} />
        <Column field="displayName" />
        <Column field="ownerFullName" style={{ width: '8em' }} />
        <Column field="ownerEmail" />
        <Column field="id" body={deleteTemplate} style={{ width: '4em' }} />
      </DataTable>
    </Dialog>
  );
};

export default AccountsInHierarchyDeleteDialog;
